/**
 * TanStack Router — typisierte Routen und Suchparameter (Paket 10 + 14).
 */

import {
  Link,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { token } from "@/lib/auth";
import { Titel } from "@/components/entwurf/Titel";
import { verweisKlassen } from "@/components/entwurf/Verweis";
import { AutoQaSeite } from "@/seiten/AutoQaSeite";
import { AutoQaLaufDetail } from "@/seiten/AutoQaLaufDetail";
import { AutoQaAuftragDetail } from "@/seiten/AutoQaAuftragDetail";
import { AutoQaAutomationDetail } from "@/seiten/AutoQaAutomationDetail";
import { Huelle } from "@/seiten/Huelle";
import { Klaerungen } from "@/seiten/Klaerungen";
import { Uebersicht } from "@/seiten/Uebersicht";
import { ProjekteListe } from "@/seiten/ProjekteListe";
import { ProjektDetail } from "@/seiten/ProjektDetail";
import { AufgabenListe } from "@/seiten/aufgaben/AufgabenListe";
import { AufgabeDetail } from "@/seiten/AufgabeDetail";
import {
  EinstellungenGitlab,
  EinstellungenRezepte,
  EinstellungenZugaenge,
  EinstellungenInstanz,
  EinstellungenPersoenlich,
  EinstellungenSichten,
  EinstellungenTeam,
  EinstellungenZeitplanung,
} from "@/seiten/einstellungen";
import { Anmeldung } from "@/seiten/Anmeldung";
import { filterAusSuche, sucheAusFilter } from "@/lib/filter";
import {
  filterAusSuche as autoQaFilterAusSuche,
  sucheAusFilter as autoQaSucheAusFilter,
} from "@/lib/auto-qa-filter";
import {
  SeitenStartGlobal,
  SeitenStartProjekt,
} from "@/seiten/seiten/SeitenStart";
import { SeiteDetail } from "@/seiten/seiten/SeiteDetail";
import { RegelnSeite } from "@/seiten/regeln/RegelnSeite";
import { Benachrichtigungen } from "@/seiten/Benachrichtigungen";
import { MetarrSeite } from "@/seiten/MetarrSeite";
import { EinstellungenMetarr } from "@/seiten/EinstellungenMetarr";
import { MeilensteinDetail } from "@/seiten/projekt/MeilensteinDetail";
import { ArbeitspaketDetail } from "@/seiten/projekt/ArbeitspaketDetail";
import { kanonisiereProjektReiter } from "@/lib/projekt-routen";
import { AKTEURSARTEN, OBJEKTARTEN, VORGAENGE } from "@cubic/shared";

function ausListe<const T extends readonly string[]>(
  roh: unknown,
  liste: T,
): T[number] | undefined {
  return typeof roh === "string" && liste.includes(roh as T[number])
    ? roh
    : undefined;
}

type ProjektSuche = {
  reiter?: string;
  erledigte?: "anzeigen";
  ansicht?: "board" | "liste" | "zeit";
  frist?: "ueberfaellig" | "heute" | "woche" | "ohne";
  q?: string;
  akteur?: (typeof AKTEURSARTEN)[number];
  vorgang?: (typeof VORGAENGE)[number];
  art?: (typeof OBJEKTARTEN)[number];
  seit?: "24h" | "7t" | "30t" | "alles";
  klaerung?: "offen" | "beantwortet" | "geklaert" | "verworfen" | "alle";
  befund?: "offen" | "alle";
  lieferung?:
    | "merge_requests"
    | "pipelines"
    | "commits"
    | "auslieferungen"
    | "unzugeordnete";
};

function validiereProjektSuche(raw: Record<string, unknown>): ProjektSuche {
  const ergebnis: ProjektSuche = {};
  if (typeof raw["reiter"] === "string") ergebnis.reiter = raw["reiter"];
  const akteur = ausListe(raw["akteur"], AKTEURSARTEN);
  const vorgang = ausListe(raw["vorgang"], VORGAENGE);
  const art = ausListe(raw["art"], OBJEKTARTEN);
  const seit = ausListe(raw["seit"], ["24h", "7t", "30t", "alles"] as const);
  const erledigte = ausListe(raw["erledigte"], ["anzeigen"] as const);
  const ansicht = ausListe(
    raw["ansicht"],
    ["board", "liste", "zeit"] as const,
  );
  const q = typeof raw["q"] === "string" ? raw["q"].trim() : "";
  const frist = ausListe(
    raw["frist"],
    ["ueberfaellig", "heute", "woche", "ohne"] as const,
  );
  const klaerung = ausListe(
    raw["klaerung"],
    ["offen", "beantwortet", "geklaert", "verworfen", "alle"] as const,
  );
  const befund = ausListe(raw["befund"], ["offen", "alle"] as const);
  const lieferung = ausListe(
    raw["lieferung"],
    [
      "merge_requests",
      "pipelines",
      "commits",
      "auslieferungen",
      "unzugeordnete",
    ] as const,
  );
  if (akteur) ergebnis.akteur = akteur;
  if (vorgang) ergebnis.vorgang = vorgang;
  if (art) ergebnis.art = art;
  if (seit) ergebnis.seit = seit;
  if (erledigte) ergebnis.erledigte = erledigte;
  if (ansicht) ergebnis.ansicht = ansicht;
  if (frist) ergebnis.frist = frist;
  if (q) ergebnis.q = q;
  if (klaerung) ergebnis.klaerung = klaerung;
  if (befund) ergebnis.befund = befund;
  if (lieferung) ergebnis.lieferung = lieferung;
  return ergebnis;
}

function verlangeToken() {
  if (!token()) {
    const von =
      typeof globalThis.location !== "undefined"
        ? globalThis.location.pathname + globalThis.location.search
        : "/";
    throw redirect({ to: "/anmeldung", search: { von } });
  }
}

/**
 * Was bei einer unbekannten Adresse erscheint.
 *
 * Ohne diese Komponente zeigt TanStack seine Vorgabe: das Wort „Not Found",
 * englisch, in der linken oberen Ecke, ohne Gerüst. Genau dort landet heute
 * jeder Klick auf **Einrichtung** in der Seitenleiste — der Eintrag zeigt auf
 * `/einrichtung`, und diese Route gibt es nicht. Wer das sieht, hält die
 * Anwendung für kaputt, obwohl vier Bereiche fertig sind.
 *
 * Der Text sagt deshalb beides: dass die Adresse ins Leere führt **und** dass
 * das an einem noch nicht gebauten Bereich liegen kann.
 */
function NichtGefunden() {
  return (
    <div className="max-w-[560px]">
      <Titel>Diesen Bereich gibt es nicht</Titel>
      <div className="mt-11 border-t border-tinte pt-[22px]">
        <p className="text-[15px] font-light leading-[1.7] text-tinte3">
          Die Adresse führt ins Leere — vertippt, oder der Bereich steht in der
          Navigation, ist aber noch nicht gebaut.
        </p>
        <div className="mt-5">
          <Link to="/" className={verweisKlassen()}>
            Zurück zu Heute
          </Link>
        </div>
      </div>
    </div>
  );
}

/*
 * ── Warum diese Komponenten hier oben stehen ──────────────────────────────
 *
 * Eine Route bekommt einen **Typ**, keine Beschreibung. Stand hier
 * `component: () => <AufgabenListe />`, dann erzeugte jeder Render des Routers
 * einen neuen Pfeil — für React ein anderer Komponententyp als beim letzten
 * Mal. Er hängt dann nicht etwa neu ab, sondern den ganzen Teilbaum: alter
 * Zustand verworfen, Effekte neu, Eingaben leer, Rollposition zurück.
 *
 * Sichtbar wurde das an den Aufgaben — Filterfenster, Anlegedialog und die
 * gewählte Ansicht leben in `useState` innerhalb von `AufgabenListe`.
 *
 * Deshalb: Wo eine Route eine Komponente ohne Zutat braucht, steht die
 * Komponente selbst. Wo sie eine Eigenschaft mitgeben muss, steht dafür eine
 * benannte Komponente auf Modulebene — einmal erzeugt, für immer derselbe Typ.
 */

/** Die Wurzel reicht nur durch. Als benannte Komponente statt als Pfeil. */
function Wurzel() {
  return <Outlet />;
}

const rootRoute = createRootRoute({
  component: Wurzel,
  notFoundComponent: NichtGefunden,
});

const anmeldungRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/anmeldung",
  validateSearch: (raw: Record<string, unknown>) => ({
    ...(typeof raw["von"] === "string" ? { von: raw["von"] } : {}),
    ...(raw["fehler"] === "rueckweg"
      ? { fehler: "rueckweg" as const }
      : {}),
  }),
  component: Anmeldung,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  beforeLoad: () => {
    verlangeToken();
  },
  component: Huelle,
});

const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: Uebersicht,
});

const projekteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projekte",
  component: ProjekteListe,
});

const projektDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projekte/$id",
  validateSearch: validiereProjektSuche,
  beforeLoad: ({ params, search }) => {
    // Seiten haben die kanonische Route aus Paket 14.
    if (search.reiter === "seiten") {
      throw redirect({
        to: "/projekte/$projektId/seiten",
        params: { projektId: params.id },
      });
    }
    // Puls/Aufgaben/Lieferung/… bleiben auf dem Projektdetail (Paket 12).
    void kanonisiereProjektReiter(params.id, search.reiter);
  },
  component: ProjektDetail,
});

const projektSeitenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projekte/$projektId/seiten",
  component: SeitenStartProjekt,
});

const projektSeiteDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projekte/$projektId/seiten/$seiteId",
  component: SeiteDetail,
});

const projektAufgabenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projekte/$projektId/aufgaben",
  component: AufgabenListe,
});

const projektRegelnRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projekte/$projektId/regeln",
  component: RegelnSeite,
});

const projektMeilensteinDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projekte/$projektId/meilensteine/$meilensteinId",
  component: MeilensteinDetail,
});

const projektArbeitspaketDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projekte/$projektId/arbeitspakete/$arbeitspaketId",
  component: ArbeitspaketDetail,
});

const seitenGlobalRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/seiten",
  component: SeitenStartGlobal,
});

const aufgabenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/aufgaben",
  validateSearch: (raw: Record<string, unknown>) => {
    const flat: Record<string, string | undefined> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v === "string") flat[k] = v;
    }
    const filterSuche = sucheAusFilter(filterAusSuche(flat));
    const ansicht = ausListe(
      raw["ansicht"],
      ["board", "liste", "zeit"] as const,
    );
    if (ansicht) filterSuche["ansicht"] = ansicht;
    return filterSuche;
  },
  component: AufgabenListe,
});

/*
 * Auto Q&A (Paket 19). Der Filterzustand steht in der Adresse — ein Link auf
 * eine gefilterte Sicht muss teilbar sein (B6).
 */
const autoQaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/auto-qa",
  validateSearch: (raw: Record<string, unknown>) => {
    const flach: Record<string, string | undefined> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v === "string") flach[k] = v;
    }
    return autoQaSucheAusFilter(autoQaFilterAusSuche(flach));
  },
  component: AutoQaSeite,
});

const autoQaAuftragRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/auto-qa/auftraege/$auftragId",
  component: AutoQaAuftragDetail,
});

/*
 * Der Lauf bleibt erreichbar: Aus der Durchsicht heraus wird weiterhin auf
 * einen einzelnen Lauf verwiesen, und ein Auftrag klammert Läufe, er ersetzt
 * sie nicht.
 */
const autoQaLaufRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/auto-qa/laeufe/$laufId",
  component: AutoQaLaufDetail,
});

/*
 * `$automationId` nimmt auch `neu` — die Maske zum Anlegen ist dieselbe wie
 * die zum Ändern (D). Eine eigene Route dafür wäre eine zweite Maske, die
 * auseinanderdriftet.
 */
const autoQaAutomationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/auto-qa/automationen/$automationId",
  component: AutoQaAutomationDetail,
});

const klaerungenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/klaerungen",
  component: Klaerungen,
});

const aufgabeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/aufgaben/$id",
  component: AufgabeDetail,
});

const einstellungenIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen",
  beforeLoad: () => {
    throw redirect({ to: "/einstellungen/persoenlich" });
  },
});

/** Alte Adressen bleiben als Weiterleitung erhalten. */
const einstAllgemeinRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/allgemein",
  beforeLoad: () => {
    throw redirect({ to: "/einstellungen/persoenlich" });
  },
});

const einstPersoenlichRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/persoenlich",
  component: EinstellungenPersoenlich,
});

const einstZeitplanungRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/zeitplanung",
  component: EinstellungenZeitplanung,
});

const einstKalenderRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/kalender",
  beforeLoad: () => {
    throw redirect({ to: "/einstellungen/zeitplanung" });
  },
});

const einstKapazitaetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/kapazitaet",
  beforeLoad: () => {
    throw redirect({ to: "/einstellungen/zeitplanung" });
  },
});

const einstSichtenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/sichten",
  component: EinstellungenSichten,
});

const einstPersonenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/personen",
  beforeLoad: () => {
    throw redirect({ to: "/einstellungen/team" });
  },
});

const einstTeamRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/team",
  component: EinstellungenTeam,
});

const einstInstanzRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/instanz",
  component: EinstellungenInstanz,
});

const einstMetarrRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/metarr",
  component: EinstellungenMetarr,
});

const einstRezepteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/rezepte",
  component: EinstellungenRezepte,
});

const einstZugaengeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/zugaenge",
  component: EinstellungenZugaenge,
});

const metarrRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/metarr",
  component: MetarrSeite,
});

const einstGitlabRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/einstellungen/gitlab",
  component: EinstellungenGitlab,
});

const benachrichtigungenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/benachrichtigungen",
  component: Benachrichtigungen,
});

const routeTree = rootRoute.addChildren([
  anmeldungRoute,
  appRoute.addChildren([
    indexRoute,
    projekteRoute,
    projektDetailRoute,
    projektSeitenRoute,
    projektSeiteDetailRoute,
    projektAufgabenRoute,
    projektRegelnRoute,
    projektMeilensteinDetailRoute,
    projektArbeitspaketDetailRoute,
    seitenGlobalRoute,
    aufgabenRoute,
    autoQaRoute,
    autoQaAuftragRoute,
    autoQaLaufRoute,
    autoQaAutomationRoute,
    klaerungenRoute,
    aufgabeDetailRoute,
    benachrichtigungenRoute,
    einstellungenIndexRoute,
    einstAllgemeinRoute,
    einstPersoenlichRoute,
    einstZeitplanungRoute,
    einstKalenderRoute,
    einstKapazitaetRoute,
    einstSichtenRoute,
    einstPersonenRoute,
    einstTeamRoute,
    einstInstanzRoute,
    einstMetarrRoute,
    metarrRoute,
    einstRezepteRoute,
    einstZugaengeRoute,
  einstGitlabRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
